// @noflow
import type { Action, State } from './types';

const initialState: State = {
  error: false,
  fetching: false,
  cards: [],
  selectedCard: {
    last_four: '',
    expiration_date: '',
    card_type: '',
    id: null,
  },
  pricingInfo: {
    pricing_plans: [],
    product_id: null,
  },
  purchasedContentId: null,
  purchaseSucceeded: false,
  purchases: {
    error: false,
    fetching: false,
    content: [],
  },
  purchasesCount: 0,
  estAuthorizedDevices: {
    fetching: false,
    error: false,
    devices: [],
  },
  removeDevice: {
    currentlyRemovingDevices: [],
    errorDescription: '',
  },
  viewContent: {
    error: false,
    fetching: false,
    content: undefined,
  },
  updateContentProgress: {
    error: false,
    fetching: false,
    content: undefined,
  },
  currentFilters: null,
  currentSort: 'purchaseDate',
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'REQUEST_PRICING_INFO':
      return {
        ...state,
        fetching: true,
      };
    case 'RECEIVE_PRICING_INFO':
      return {
        ...state,
        fetching: false,
        pricingInfo: action.payload,
      };
    case 'REQUEST_PRICING_INFO_ERROR':
      return {
        ...state,
        fetching: false,
        error: true,
      };
    case 'REQUEST_PAYMENT_INFO':
      return {
        ...state,
        fetching: true,
      };
    case 'RECEIVE_PAYMENT_INFO':
      return {
        ...state,
        fetching: false,
        cards: action.payload,
      };
    case 'REQUEST_PAYMENT_INFO_ERROR':
      return {
        ...state,
        fetching: false,
        error: true,
      };
    case 'SELECT_CARD':
      return {
        ...state,
        selectedCard: action.payload,
      };
    case 'REQUEST_PURCHASE':
      return {
        ...state,
        fetching: true,
        purchaseSucceeded: false,
      };
    case 'PURCHASE_SUCCESS':
      return {
        ...state,
        fetching: false,
        purchaseSucceeded: true,
        purchasedContentId: action.contentId,
      };
    case 'PURCHASE_FAILURE':
      return {
        ...state,
        fetching: false,
        purchaseSucceeded: false,
        error: true,
      };
    case 'REQUEST_PURCHASES':
      return {
        ...state,
        purchases: {
          fetching: true,
          content: [],
          error: false,
        },
      };
    case 'RECEIVE_PURCHASES':
      return {
        ...state,
        purchases: {
          fetching: false,
          content: action.payload.purchases,
          error: false,
        },
        purchasesCount: action.payload.count,
      };
    case 'RECEIVE_PURCHASES_ERROR':
      return {
        ...state,
        purchases: {
          fetching: false,
          content: [],
          error: true,
        },
        purchasesCount: 0,
      };
    case 'REQUEST_EST_AUTHORIZED_DEVICES': {
      return {
        ...state,
        estAuthorizedDevices: {
          ...state.estAuthorizedDevices,
          fetching: true,
          error: false,
        },
      };
    }
    case 'RECEIVE_EST_AUTHORIZED_DEVICES': {
      const { payload } = action;
      return {
        ...state,
        estAuthorizedDevices: {
          fetching: false,
          error: false,
          devices: payload,
        },
      };
    }
    case 'REQUEST_EST_AUTHORIZED_DEVICES_ERROR': {
      return {
        ...state,
        estAuthorizedDevices: {
          ...state.estAuthorizedDevices,
          fetching: false,
          error: true,
        },
      };
    }
    case 'REQUEST_REMOVE_EST_DEVICE': {
      const { payload: deviceId } = action;
      return {
        ...state,
        removeDevice: {
          ...state.removeDevice,
          currentlyRemovingDevices: [...state.removeDevice.currentlyRemovingDevices, deviceId],
        },
      };
    }
    case 'RECEIVE_REMOVE_EST_DEVICE': {
      const { payload: deviceId } = action;
      return {
        ...state,
        removeDevice: {
          errorDescription: '',
          currentlyRemovingDevices: state.removeDevice.currentlyRemovingDevices.filter(
            item => item !== deviceId,
          ),
        },
        estAuthorizedDevices: {
          ...state.estAuthorizedDevices,
          devices: state.estAuthorizedDevices.devices.filter(item => item.device_id !== deviceId),
        },
      };
    }
    case 'REQUEST_REMOVE_EST_DEVICE_ERROR': {
      const { errorDescription, deviceId } = action.payload;
      return {
        ...state,
        removeDevice: {
          errorDescription,
          currentlyRemovingDevices: state.removeDevice.currentlyRemovingDevices.filter(
            item => item !== deviceId,
          ),
        },
      };
    }
    case 'CLEAR_REMOVE_EST_DEVICE_ERROR_MESSAGE': {
      return {
        ...state,
        removeDevice: {
          ...state.removeDevice,
          errorDescription: '',
        },
      };
    }
    case 'REQUEST_EST_VIEW_CONTENT': {
      return {
        ...state,
        viewContent: {
          ...state.viewContent,
          error: false,
          fetching: true,
        },
      };
    }
    case 'RECEIVE_EST_VIEW_CONTENT_ERROR': {
      return {
        ...state,
        viewContent: {
          ...state.viewContent,
          error: true,
          fetching: false,
        },
      };
    }
    case 'RECEIVE_EST_VIEW_CONTENT': {
      return {
        ...state,
        viewContent: {
          error: false,
          fetching: false,
          content: action.payload,
        },
      };
    }
    case 'REQUEST_UPDATE_CONTENT_PROGRESS': {
      return {
        ...state,
        updateContentProgress: {
          ...state.updateContentProgress,
          fetching: true,
          error: false,
        },
      };
    }
    case 'RECEIVE_UPDATE_CONTENT_PROGRESS': {
      return {
        ...state,
        updateContentProgress: {
          ...state.updateContentProgress,
          fetching: false,
          error: false,
          content: action.payload,
        },
      };
    }
    case 'RECEIVE_UPDATE_CONTENT_PROGRESS_ERROR': {
      return {
        ...state,
        updateContentProgress: {
          ...state.updateContentProgress,
          fetching: false,
          error: true,
        },
      };
    }
    case 'UPDATE_SORT': {
      return {
        ...state,
        currentSort: action.payload,
      };
    }
    case 'REQUEST_REGISTER_EST_DEVICE':
      return { ...state, fetching: true, error: false };
    case 'RECEIVE_REGISTER_EST_DEVICE':
      return { ...state, fetching: false, error: false };
    case 'RECEIVE_REGISTER_EST_DEVICE_ERROR':
      return { ...state, fetching: false, error: true };
    // $FlowExpectedError
    case 'SIGN_OUT':
      /* We must clean all user and STB related data from store on sign-out */
      return { ...initialState };
    default:
      /* Following line assures switch exhaustiveness */
      (action.type: empty); /* eslint-disable-line */
      return state;
  }
};

export default reducer;
